import React, { useState } from 'react';
import { BookOpen, Code, Copy, Check, Shield, Database } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TechnicalDocumentation: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('architecture');
  const [copiedEndpoint, setCopiedEndpoint] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(id);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const apiSections = {
    architecture: {
      title: 'Architecture',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-600 p-3 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Système Multi-Tenant</h3>
                <p className="text-gray-700 mb-4">
                  Clareefai utilise une architecture multi-tenant où chaque vendor (client) dispose de données complètement isolées.
                </p>
                <div className="bg-white rounded-lg p-4 space-y-3">
                  <div className="flex items-start space-x-3">
                    <Database className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Isolation des Données</h4>
                      <p className="text-sm text-gray-600">
                        Chaque requête API est automatiquement filtrée par le <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">vendorId</code> du client authentifié.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Sécurité</h4>
                      <p className="text-sm text-gray-600">
                        Les données d'un vendor ne sont JAMAIS accessibles par un autre vendor. Le filtrage est appliqué au niveau de la base de données avec RLS (Row Level Security).
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Structure de Base de Données</h3>
            <p className="text-gray-600 mb-4">Chaque table principale contient une colonne <code className="bg-gray-100 px-2 py-1 rounded">vendor_id</code>:</p>
            <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
{`-- Exemple de table promoters
CREATE TABLE promoters (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  vendor_id UUID NOT NULL REFERENCES vendors(id),
  name TEXT NOT NULL,
  email TEXT NOT NULL,
  company TEXT NOT NULL,
  status TEXT NOT NULL,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  UNIQUE(vendor_id, email)
);

-- RLS Policy pour isolation
CREATE POLICY "Vendors can only access their own promoters"
  ON promoters FOR ALL
  USING (vendor_id = auth.jwt() -> 'app_metadata' ->> 'vendor_id');`}
            </pre>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Authentification & Vendor ID</h3>
            <p className="text-gray-600 mb-4">Le <code className="bg-gray-100 px-2 py-1 rounded">vendorId</code> est automatiquement extrait du JWT token:</p>
            <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
{`// Header de requête
Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...

// JWT Payload contient:
{
  "sub": "user-uuid",
  "email": "user@example.com",
  "app_metadata": {
    "vendor_id": "vendor-uuid-123",
    "role": "admin"
  }
}

// Le vendorId est automatiquement utilisé pour filtrer toutes les requêtes`}
            </pre>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-amber-900 mb-3">Important</h3>
            <ul className="space-y-2 text-amber-800">
              <li className="flex items-start space-x-2">
                <span className="font-bold">•</span>
                <span>Vous ne devez JAMAIS spécifier manuellement le vendorId dans vos requêtes</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-bold">•</span>
                <span>Le filtrage par vendor est automatique et transparent</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-bold">•</span>
                <span>All URLs below return only YOUR data</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    dashboard: {
      title: 'Dashboard',
      endpoints: [
        {
          id: 'dashboard-stats',
          method: 'GET',
          url: '/api/dashboard/stats',
          description: 'Obtenir les statistiques du dashboard',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            params: {
              period: 'string (optional) - week, month, year'
            }
          },
          response: {
            status: 200,
            body: {
              stats: {
                totalMeetings: 8,
                activePromoters: 24,
                totalProspects: 15,
                conversionRate: '68%'
              },
              performanceStats: [
                {
                  title: 'Page views',
                  value: '1,247',
                  change: '+12% this month',
                  icon: 'Eye',
                  color: 'bg-blue-50 text-blue-600'
                }
              ]
            }
          }
        }
      ]
    },
    promoters: {
      title: 'Promoters',
      endpoints: [
        {
          id: 'promoters-list',
          method: 'GET',
          url: '/api/promoters',
          description: 'Liste de tous vos promoters',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            params: {
              status: 'string (optional) - invited, review, available, testimonial_ready, testimonial_done',
              search: 'string (optional) - Recherche par nom ou entreprise',
              page: 'number (optional) - Numéro de page',
              limit: 'number (optional) - Items par page'
            }
          },
          response: {
            status: 200,
            body: {
              promoters: [
                {
                  id: '1',
                  name: 'Michel Dubois',
                  email: 'michel.dubois@company.fr',
                  phone: '+33 1 23 45 67 89',
                  company: 'Tech Solutions',
                  avatar: 'https://...',
                  status: 'invited',
                  prospectsReferred: 0,
                  conversionRate: 0,
                  totalRewards: 0,
                  joinedDate: '2024-01-10',
                  sectors: ['Technology', 'Services'],
                  companySize: '51-200 employees'
                }
              ],
              stats: {
                total: 30,
                invited: 8,
                readyForCall: 5,
                totalRewards: 15800
              }
            }
          }
        },
        {
          id: 'promoters-create',
          method: 'POST',
          url: '/api/promoters',
          description: 'Créer un nouveau promoter',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            body: {
              name: 'string (required)',
              email: 'string (required)',
              company: 'string (required)',
              phone: 'string (optional)',
              sectors: 'array (optional)',
              companySize: 'string (optional)',
              status: 'string (optional) - Default: invited'
            }
          },
          response: {
            status: 201,
            body: {
              id: 'string',
              name: 'string',
              email: 'string',
              company: 'string',
              status: 'invited',
              joinedDate: 'ISO 8601 date string'
            }
          }
        },
        {
          id: 'promoters-detail',
          method: 'GET',
          url: '/api/promoters/:id',
          description: 'Détails d\'un promoter spécifique',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            }
          },
          response: {
            status: 200,
            body: {
              id: 'string',
              name: 'string',
              email: 'string',
              company: 'string',
              status: 'string',
              prospectsReferred: 'number',
              conversionRate: 'number',
              totalRewards: 'number',
              sectors: 'array',
              companySize: 'string',
              joinedDate: 'ISO 8601 date string'
            }
          }
        }
      ]
    },
    prospects: {
      title: 'Prospects',
      endpoints: [
        {
          id: 'prospects-list',
          method: 'GET',
          url: '/api/prospects',
          description: 'Liste de tous vos prospects',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            params: {
              status: 'string (optional) - introduce, request_sent, scheduled, completed, failed',
              promoterId: 'string (optional) - Filtrer par promoter',
              assignedTo: 'string (optional) - Filtrer par commercial',
              page: 'number (optional)',
              limit: 'number (optional)'
            }
          },
          response: {
            status: 200,
            body: {
              prospects: [
                {
                  id: '1',
                  name: 'Sarah Johnson',
                  email: 'sarah.johnson@techcorp.com',
                  phone: '+33 1 56 78 90 23',
                  company: 'Digital Corp',
                  promoterId: '2',
                  promoterName: 'Marie Leroy',
                  assignedPromoters: ['2', '3', '4'],
                  status: 'request_sent',
                  assignedTo: 'Commercial B',
                  assignedSalespersonId: 'tm2',
                  createdDate: '2024-01-19',
                  notes: 'Recherche une solution pour optimiser leur workflow',
                  sectors: ['Technology', 'AI'],
                  companySize: '51-200 employees'
                }
              ],
              stats: {
                total: 34,
                introduce: 8,
                request_sent: 6,
                scheduled: 6,
                failed: 5,
                completed: 9
              }
            }
          }
        },
        {
          id: 'prospects-create',
          method: 'POST',
          url: '/api/prospects',
          description: 'Créer un nouveau prospect',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            body: {
              name: 'string (required)',
              email: 'string (required)',
              company: 'string (required)',
              phone: 'string (optional)',
              promoterId: 'string (optional)',
              notes: 'string (optional)',
              sectors: 'array (optional)',
              companySize: 'string (optional)'
            }
          },
          response: {
            status: 201,
            body: {
              id: 'string',
              name: 'string',
              email: 'string',
              status: 'introduce',
              createdDate: 'ISO 8601 date string'
            }
          }
        },
        {
          id: 'prospects-widget',
          method: 'GET',
          url: '/api/prospects/widget-submissions',
          description: 'Obtenir les soumissions via widget',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            }
          },
          response: {
            status: 200,
            body: {
              widgetSubmissions: [
                {
                  id: 'w1',
                  name: 'Jennifer Martinez',
                  email: 'jennifer.martinez@techcorp.com',
                  phone: '+1 555 123 4567',
                  company: 'TechCorp Solutions',
                  requestedPromoter: 'Marie Leroy',
                  requestedDate: '2024-02-05',
                  requestedTime: '14:00',
                  submissionDate: '2024-01-28',
                  status: 'pending_validation',
                  widget: 'Homepage Widget',
                  notes: 'Interested in Clareefai Platform'
                }
              ]
            }
          }
        }
      ]
    },
    meetings: {
      title: 'Meetings',
      endpoints: [
        {
          id: 'meetings-list',
          method: 'GET',
          url: '/api/meetings',
          description: 'Liste de tous vos meetings',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            params: {
              status: 'string (optional) - request, progress, booked, completed',
              startDate: 'string (optional) - ISO date',
              endDate: 'string (optional) - ISO date',
              promoterId: 'string (optional)',
              page: 'number (optional)'
            }
          },
          response: {
            status: 200,
            body: {
              meetings: [
                {
                  id: '1',
                  prospectName: 'Sarah Johnson',
                  promoterName: 'Michel Dubois',
                  date: '2024-01-15',
                  time: '14:00',
                  email: 'sarah.johnson@techcorp.com',
                  status: 'request'
                }
              ]
            }
          }
        },
        {
          id: 'meetings-create',
          method: 'POST',
          url: '/api/meetings',
          description: 'Planifier un nouveau meeting',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            body: {
              prospectName: 'string (required)',
              promoterName: 'string (required)',
              date: 'string (required) - ISO date',
              time: 'string (required) - HH:MM',
              email: 'string (required)',
              notes: 'string (optional)'
            }
          },
          response: {
            status: 201,
            body: {
              id: 'string',
              prospectName: 'string',
              promoterName: 'string',
              date: 'string',
              status: 'request'
            }
          }
        }
      ]
    },
    testimonials: {
      title: 'Testimonials',
      endpoints: [
        {
          id: 'testimonials-list',
          method: 'GET',
          url: '/api/testimonials',
          description: 'Liste de tous vos témoignages vidéo',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            params: {
              status: 'string (optional) - sent, pending_review, approved, published',
              quality: 'string (optional) - high, medium, low',
              promoterId: 'string (optional)',
              page: 'number (optional)'
            }
          },
          response: {
            status: 200,
            body: {
              ourRequests: [
                {
                  id: '1',
                  promoterName: 'Michel Dubois',
                  promoterCompany: 'Tech Solutions',
                  promoterAvatar: 'https://...',
                  requestDate: '2024-01-15',
                  status: 'sent',
                  emailSent: true,
                  reminderCount: 1,
                  lastReminder: '2024-01-20',
                  product: 'Clareefai Analytics',
                  conversionValue: 15000
                }
              ],
              promoterSubmissions: [
                {
                  id: '5',
                  promoterName: 'Isabelle Petit',
                  promoterCompany: 'HealthTech Innovations',
                  title: 'Innovation in healthcare sector',
                  content: 'Clareefai helps us...',
                  videoUrl: 'https://...',
                  duration: '4:20',
                  submissionDate: '2024-02-01',
                  status: 'pending_review',
                  quality: 'high',
                  product: 'Clareefai Health',
                  sector: 'Healthcare'
                }
              ],
              featured: [
                {
                  id: '9',
                  promoterName: 'Marie Leroy',
                  title: 'A revolutionary solution',
                  content: 'Since we started using Clareefai...',
                  videoUrl: 'https://...',
                  duration: '2:30',
                  views: 1247,
                  likes: 89,
                  shares: 23,
                  publishDate: '2024-01-20',
                  featured: true,
                  status: 'published'
                }
              ]
            }
          }
        },
        {
          id: 'testimonials-request',
          method: 'POST',
          url: '/api/testimonials/request',
          description: 'Send a testimonial request',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            body: {
              promoterId: 'string (required)',
              product: 'string (required)',
              instructions: 'string (optional)',
              deadline: 'ISO 8601 date string (optional)'
            }
          },
          response: {
            status: 201,
            body: {
              id: 'string',
              promoterId: 'string',
              status: 'sent',
              sentAt: 'ISO 8601 date string'
            }
          }
        }
      ]
    },
    useCases: {
      title: 'Use Cases',
      endpoints: [
        {
          id: 'usecases-list',
          method: 'GET',
          url: '/api/use-cases',
          description: 'Liste de tous vos use cases',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            params: {
              status: 'string (optional) - sent, pending_review, published, featured',
              industry: 'string (optional)',
              promoterId: 'string (optional)',
              page: 'number (optional)'
            }
          },
          response: {
            status: 200,
            body: {
              ourRequests: [
                {
                  id: '1',
                  promoterName: 'Michel Dubois',
                  promoterCompany: 'Tech Solutions',
                  requestDate: '2024-01-15',
                  status: 'sent',
                  emailSent: true,
                  product: 'Clareefai Analytics',
                  industry: 'Technology',
                  companySize: '51-200 employees'
                }
              ],
              promoterSubmissions: [
                {
                  id: '5',
                  promoterName: 'Isabelle Petit',
                  promoterCompany: 'HealthTech Innovations',
                  title: 'Innovation in healthcare sector',
                  content: 'Clareefai Analytics helped us...',
                  submissionDate: '2024-02-01',
                  status: 'pending_review',
                  quality: 'high',
                  product: 'Clareefai Analytics',
                  industry: 'Healthcare',
                  benefits: ['Reduced waiting time', 'Improved satisfaction']
                }
              ],
              published: [
                {
                  id: '9',
                  title: 'Optimization of Patient Pathways',
                  promoterName: 'Dr. Sarah Johnson',
                  industry: 'Healthcare',
                  description: 'A large hospital center uses Clareefai...',
                  benefits: ['Reduced waiting time', 'Resource optimization'],
                  publishDate: '2024-01-20',
                  views: 1247,
                  downloads: 89,
                  shares: 23,
                  status: 'published'
                }
              ]
            }
          }
        },
        {
          id: 'usecases-request',
          method: 'POST',
          url: '/api/use-cases/request',
          description: 'Demander un use case à un promoter',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            body: {
              promoterId: 'string (required)',
              product: 'string (required)',
              industry: 'string (required)',
              instructions: 'string (optional)'
            }
          },
          response: {
            status: 201,
            body: {
              id: 'string',
              promoterId: 'string',
              status: 'sent',
              sentAt: 'ISO 8601 date string'
            }
          }
        }
      ]
    },
    reviews: {
      title: 'Reviews',
      endpoints: [
        {
          id: 'reviews-list',
          method: 'GET',
          url: '/api/reviews',
          description: 'Liste de tous vos avis clients',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            params: {
              rating: 'number (optional) - 1-5',
              status: 'string (optional) - pending, approved',
              featured: 'boolean (optional)',
              page: 'number (optional)'
            }
          },
          response: {
            status: 200,
            body: {
              reviews: [
                {
                  id: '1',
                  author: 'Michel Dubois',
                  company: 'Tech Solutions',
                  rating: 5,
                  comment: 'Excellent solution...',
                  product: 'Clareefai Analytics',
                  sector: 'Technology',
                  companySize: '51-200 employees',
                  hasVideo: false,
                  hasImage: true,
                  date: '2024-01-15',
                  featured: true,
                  verified: true,
                  helpful: 24,
                  status: 'approved',
                  companyResponse: 'Thank you Michel!'
                }
              ],
              stats: {
                total: 8,
                featured: 3,
                verified: 7,
                pending: 1,
                averageRating: '4.8',
                withResponse: 5
              }
            }
          }
        },
        {
          id: 'reviews-respond',
          method: 'POST',
          url: '/api/reviews/:id/respond',
          description: 'Répondre à un avis',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            body: {
              response: 'string (required)',
              sendEmail: 'boolean (optional) - Default: true'
            }
          },
          response: {
            status: 200,
            body: {
              id: 'string',
              status: 'approved',
              companyResponse: 'string',
              respondedAt: 'ISO 8601 date string'
            }
          }
        }
      ]
    },
    rewards: {
      title: 'Rewards',
      endpoints: [
        {
          id: 'rewards-list',
          method: 'GET',
          url: '/api/rewards',
          description: 'Liste de tous vos rewards',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            params: {
              type: 'string (optional) - global, specific, payment',
              status: 'string (optional) - completed, pending, sent, paid',
              promoterId: 'string (optional)',
              page: 'number (optional)'
            }
          },
          response: {
            status: 200,
            body: {
              rewardTypes: [
                {
                  id: '1',
                  name: 'Write a review',
                  description: 'Leave a detailed review',
                  amount: 10,
                  icon: 'star',
                  color: 'bg-blue-100 text-blue-600'
                }
              ],
              globalRewards: [
                {
                  id: '1',
                  promoterName: 'Sophie Durand',
                  promoterCompany: 'Innovation Lab',
                  rewardType: 'Prospect conversion',
                  amount: 500,
                  date: '2024-01-15',
                  status: 'completed',
                  prospectName: 'Emma Brown',
                  prospectCompany: 'Enterprise Solutions'
                }
              ],
              specificRewards: [
                {
                  id: '1',
                  promoterName: 'Thomas Bernard',
                  rewardType: 'BONUS',
                  amount: 200,
                  reason: 'Exceptional performance',
                  date: '2024-01-25',
                  status: 'sent',
                  emailSent: true
                }
              ],
              payments: [
                {
                  id: '1',
                  promoterName: 'Sophie Durand',
                  amount: 1250,
                  date: '2024-01-31',
                  status: 'paid',
                  method: 'Bank Transfer',
                  reference: 'PAY-2024-001'
                }
              ],
              analytics: {
                totalPaid: 15750,
                totalPending: 3200,
                averageReward: 285,
                topRewardType: 'Prospect Conversion'
              }
            }
          }
        },
        {
          id: 'rewards-assign',
          method: 'POST',
          url: '/api/rewards/assign',
          description: 'Assigner un reward spécifique',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            body: {
              promoterId: 'string (required)',
              amount: 'number (required)',
              reason: 'string (required)',
              sendEmail: 'boolean (optional) - Default: true'
            }
          },
          response: {
            status: 201,
            body: {
              id: 'string',
              promoterId: 'string',
              amount: 'number',
              status: 'sent',
              assignedAt: 'ISO 8601 date string'
            }
          }
        }
      ]
    },
    teams: {
      title: 'Teams',
      endpoints: [
        {
          id: 'teams-list',
          method: 'GET',
          url: '/api/teams/members',
          description: 'Liste de tous vos membres d\'équipe',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            params: {
              role: 'string (optional) - admin, sales, support',
              status: 'string (optional) - active, inactive',
              page: 'number (optional)'
            }
          },
          response: {
            status: 200,
            body: {
              members: [
                {
                  id: 'tm1',
                  name: 'Commercial A',
                  email: 'commercial.a@company.com',
                  role: 'sales',
                  status: 'active',
                  assignedProspects: 12,
                  conversions: 8,
                  conversionRate: 67
                }
              ]
            }
          }
        },
        {
          id: 'teams-invite',
          method: 'POST',
          url: '/api/teams/invite',
          description: 'Inviter un nouveau membre',
          request: {
            headers: {
              'Authorization': 'Bearer {token}',
              'Content-Type': 'application/json'
            },
            body: {
              email: 'string (required)',
              role: 'string (required) - admin, sales, support',
              name: 'string (optional)'
            }
          },
          response: {
            status: 201,
            body: {
              id: 'string',
              email: 'string',
              role: 'string',
              invitedAt: 'ISO 8601 date string'
            }
          }
        }
      ]
    }
  };

  const renderEndpoint = (endpoint: any) => {
    const methodColors: { [key: string]: string } = {
      GET: 'bg-blue-100 text-blue-800',
      POST: 'bg-green-100 text-green-800',
      PUT: 'bg-yellow-100 text-yellow-800',
      DELETE: 'bg-red-100 text-red-800'
    };

    return (
      <div key={endpoint.id} className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <span className={`px-3 py-1 rounded-md text-xs font-bold ${methodColors[endpoint.method]}`}>
                {endpoint.method}
              </span>
              <code className="px-3 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                {endpoint.url}
              </code>
            </div>
            <p className="text-gray-600 text-sm">{endpoint.description}</p>
          </div>
          <button
            onClick={() => handleCopy(endpoint.url, endpoint.id)}
            className="ml-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            title="Copy URL"
          >
            {copiedEndpoint === endpoint.id ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>

        <div className="space-y-4">
          {endpoint.request.headers && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <Code className="w-4 h-4 mr-2" />
                Request
              </h4>
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-700 mb-1">Headers:</p>
                <pre className="bg-gray-50 rounded p-3 text-xs overflow-x-auto border border-gray-200">
                  <code>{JSON.stringify(endpoint.request.headers, null, 2)}</code>
                </pre>
              </div>
            </div>
          )}

          {endpoint.request.params && (
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-700 mb-1">Query Parameters:</p>
              <div className="bg-gray-50 rounded p-3 border border-gray-200">
                {Object.entries(endpoint.request.params).map(([key, value]) => (
                  <div key={key} className="text-xs mb-1">
                    <code className="text-purple-600 font-medium">{key}</code>: <span className="text-gray-600">{value as string}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {endpoint.request.body && (
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">Request Body:</p>
              <div className="bg-gray-50 rounded p-3 border border-gray-200">
                {Object.entries(endpoint.request.body).map(([key, value]) => (
                  <div key={key} className="text-xs mb-1">
                    <code className="text-purple-600 font-medium">{key}</code>: <span className="text-gray-600">{value as string}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Response</h4>
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-xs text-gray-500">Status Code:</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                {endpoint.response.status}
              </span>
            </div>
            <pre className="bg-gray-50 rounded p-3 text-xs overflow-x-auto border border-gray-200">
              <code>{JSON.stringify(endpoint.response.body, null, 2)}</code>
            </pre>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BookOpen className="w-6 h-6 mr-3 text-blue-600" />
            {t('settings.technicalDocs.pageHeading')}
          </h1>
          <p className="text-gray-600 mt-1">{t('settings.technicalDocs.pageIntro')}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex space-x-1 p-2 min-w-max">
            {Object.entries(apiSections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  activeTab === key
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6 bg-gray-50">
          {Object.entries(apiSections).map(([key, section]) => (
            activeTab === key && (
              <div key={key}>
                {section.content ? (
                  section.content
                ) : (
                  <>
                    <div className="mb-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-2">{section.title} API</h2>
                      <p className="text-gray-600 text-sm">
                        Total Endpoints: <span className="font-semibold text-blue-600">{section.endpoints?.length || 0}</span>
                      </p>
                    </div>
                    {section.endpoints?.map(endpoint => renderEndpoint(endpoint))}
                  </>
                )}
              </div>
            )
          ))}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">{t('settings.technicalDocs.generalInfo')}</h3>
        <div className="space-y-3 text-sm">
          <div className="flex items-start">
            <span className="font-medium text-blue-900 mr-2 min-w-[140px]">{t('settings.technicalDocs.baseUrl')}</span>
            <code className="bg-white px-2 py-1 rounded text-blue-700 font-mono">https://api.clareefai.com/v1</code>
          </div>
          <div className="flex items-start">
            <span className="font-medium text-blue-900 mr-2 min-w-[140px]">{t('settings.technicalDocs.authentication')}</span>
            <span className="text-blue-800">{t('settings.technicalDocs.authenticationValue')}</span>
          </div>
          <div className="flex items-start">
            <span className="font-medium text-blue-900 mr-2 min-w-[140px]">{t('settings.technicalDocs.vendorIsolation')}</span>
            <span className="text-blue-800">{t('settings.technicalDocs.vendorIsolationValue')}</span>
          </div>
          <div className="flex items-start">
            <span className="font-medium text-blue-900 mr-2 min-w-[140px]">{t('settings.technicalDocs.rateLimiting')}</span>
            <span className="text-blue-800">{t('settings.technicalDocs.rateLimitingValue')}</span>
          </div>
          <div className="flex items-start">
            <span className="font-medium text-blue-900 mr-2 min-w-[140px]">{t('settings.technicalDocs.responseFormat')}</span>
            <span className="text-blue-800">JSON</span>
          </div>
          <div className="flex items-start">
            <span className="font-medium text-blue-900 mr-2 min-w-[140px]">{t('settings.technicalDocs.timezone')}</span>
            <span className="text-blue-800">{t('settings.technicalDocs.timezoneValue')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalDocumentation;
